// utils/textRank.js
import nlp from 'compromise'; // Reusing the existing dependency

/**
 * TextRank algorithm for keyword extraction
 * Based on the PageRank algorithm but applied to words and phrases in text
 * @param {string} text - The input text
 * @returns {string[]} - Array of extracted keywords and phrases
 */
export const extractKeywordsWithTextRank = (text) => {
  if (!text || text.trim().length === 0) return [];
  
  console.log("Starting TextRank extraction...");
  
  // Enhanced stopword list with HTML variants and additional common terms
  const stopwords = new Set([
    // Articles
    'a', 'an', 'the', 'the&nbsp;',
    // Pronouns
    'he', 'he&nbsp;', 'she', 'it', 'they', 'we', 'i', 'you', 'me', 'him', 'her', 'them', 'us', 'my', 'your', 'his', 'their', 'our',
    'he\'s', 'she\'s', 'it\'s', 'they\'re', 'we\'re', 'i\'m', 'you\'re', 'i\'ve', 'you\'ve', 'they\'ve', 'we\'ve',
    // Prepositions
    'in', 'on', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down', 'of',
    // Conjunctions
    'and', 'but', 'or', 'nor', 'so', 'yet', 'because', 'although', 'since', 'unless',
    // Common verbs
    'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might',
    // Common adverbs
    'very', 'really', 'quite', 'just', 'too', 'enough', 'even', 'only', 'almost', 'also',
    // Other common words
    'this', 'that', 'these', 'those', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'some', 'such', 'no', 'not', 'only', 'own', 'same', 'too', 'very',
    // Blank space and HTML entities
    '', ' ', '&nbsp;',
    // Numbers and common fillers
    'one', 'two', 'three', 'four', 'five', 'first', 'second', 'third', 'last', 'next', 'many', 'much',
    // Common but low-value terms
    'time', 'way', 'thing', 'part', 'day', 'year', 'years', 'week', 'month',
    'example', 'fact', 'point', 'case', 'issue', 'kind', 'sort', 'type',
    'good', 'bad', 'high', 'low', 'big', 'small', 'long', 'short', 'other'
  ]);
  
  /**
   * Clean text by removing HTML entities, punctuation, extra spaces, etc.
   * @param {string} text - Text to clean
   * @returns {string} - Cleaned text
   */
  const cleanTextItem = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/&nbsp;/g, ' ')        // Replace HTML non-breaking spaces
      .replace(/&[a-z]+;/g, ' ')      // Replace other HTML entities
      .replace(/[,.!?;:()[\]{}'"]+$/, '') // Remove trailing punctuation
      .replace(/^[,.!?;:()[\]{}'"]+/, '') // Remove leading punctuation
      .replace(/\s+/g, ' ')           // Normalize multiple spaces
      .trim();
  };
  
  /**
   * Check if a term is likely to be substantive (not just noise)
   * @param {string} term - Term to check
   * @returns {boolean} - Whether term appears substantive
   */
  const isSubstantiveTerm = (term) => {
    const cleaned = cleanTextItem(term);
    
    // Check if it's a stopword
    if (stopwords.has(cleaned)) return false;
    
    // Check if it's too short
    if (cleaned.length < 3) return false;
    
    // Check if it's just a single letter or number with HTML entity
    if (/^[a-z0-9](&nbsp;)?$/.test(cleaned)) return false;
    
    // Check if it contains any alphabetic characters (not just symbols/numbers)
    if (!/[a-z]/.test(cleaned)) return false;
    
    return true;
  };
  
  // 1. Clean the text and split into sentences
  const cleanText = text.toLowerCase().replace(/[\r\n]+/g, '. ');
  const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  // 2. Extract candidate words (focusing on content words)
  const doc = nlp(cleanText);
  
  // Extract all nouns, proper nouns, and adjectives
  let candidates = new Set();
  
  // Get single words (nouns, proper nouns, adjectives)
  const contentWords = doc.match('#Noun|#Adjective').out('array');
  contentWords.forEach(word => {
    const w = cleanTextItem(word);
    if (isSubstantiveTerm(w)) {
      candidates.add(w);
    }
  });
  
  // Get noun phrases (more likely to be meaningful)
  const nounPhrases = doc.match('#Adjective? #Noun+').out('array');
  nounPhrases.forEach(phrase => {
    const p = cleanTextItem(phrase);
    if (p.length > 3 && !p.split(' ').every(w => stopwords.has(w))) {
      candidates.add(p);
    }
  });
  
  // Get entities (named entities are often important)
  const entities = doc.match('#Organization|#Person|#Place').out('array');
  entities.forEach(entity => {
    const e = cleanTextItem(entity);
    if (e.length > 2 && !stopwords.has(e)) {
      candidates.add(e);
    }
  });
  
  // 3. Add important header terms
  // Split text into lines to look for headers
  const lines = text.split('\n').filter(line => line.trim().length > 0);
  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    // Look for numbered headers (1., 2., etc.)
    if (/^\d+\.\s+/.test(trimmedLine)) {
      // Extract the header text without the number
      const headerText = trimmedLine.replace(/^\d+\.\s+/, '');
      
      // Get key terms from the header
      const headerDoc = nlp(headerText);
      const headerNouns = headerDoc.match('#Noun').out('array');
      
      headerNouns.forEach(noun => {
        const cleanNoun = cleanTextItem(noun);
        if (isSubstantiveTerm(cleanNoun)) {
          candidates.add(cleanNoun);
          
          // Give extra weight to these terms by adding them multiple times
          candidates.add(cleanNoun);
          candidates.add(cleanNoun);
        }
      });
      
      // For headers with dashes/hyphens, get the term after the dash too
      if (/—|-|–/.test(headerText)) {
        const afterDash = headerText.split(/—|-|–/)[1]?.trim();
        if (afterDash) {
          const dashDoc = nlp(afterDash);
          const dashNouns = dashDoc.match('#Noun').out('array');
          
          dashNouns.forEach(noun => {
            const cleanNoun = cleanTextItem(noun);
            if (isSubstantiveTerm(cleanNoun)) {
              // Add it with even more weight for being after a dash in a header
              candidates.add(cleanNoun);
              candidates.add(cleanNoun);
              candidates.add(cleanNoun);
              candidates.add(cleanNoun);
            }
          });
        }
      }
    }
  });
  
  // Convert to array
  const candidateArray = Array.from(candidates);
  
  // 4. Build the co-occurrence graph
  const graph = buildCoOccurrenceGraph(candidateArray, sentences);
  
  // 5. Apply the TextRank algorithm
  const scores = applyTextRank(graph);
  
  // 6. Sort candidates by score and return top results
  const rankedCandidates = candidateArray
    .map(candidate => ({
      text: candidate,
      score: scores.get(candidate) || 0
    }))
    .sort((a, b) => b.score - a.score)
    .map(item => item.text);
  
  // Return top 15 keywords
  return rankedCandidates.slice(0, 15);
};

/**
 * Build a co-occurrence graph of candidates
 * @param {string[]} candidates - Array of candidate words/phrases
 * @param {string[]} sentences - Array of sentences from the text
 * @returns {Map} - A graph represented as an adjacency list
 */
function buildCoOccurrenceGraph(candidates, sentences) {
  const graph = new Map();
  
  // Initialize graph with empty adjacency lists
  candidates.forEach(candidate => {
    graph.set(candidate, new Map());
  });
  
  // For each sentence, find co-occurring candidates
  sentences.forEach(sentence => {
    // Find all candidates that appear in this sentence
    const sentenceCandidates = candidates.filter(candidate => 
      sentence.includes(candidate)
    );
    
    // Create edges between all pairs of candidates in this sentence
    for (let i = 0; i < sentenceCandidates.length; i++) {
      for (let j = i + 1; j < sentenceCandidates.length; j++) {
        const candidate1 = sentenceCandidates[i];
        const candidate2 = sentenceCandidates[j];
        
        // Add edge from candidate1 to candidate2
        if (graph.has(candidate1)) {
          const edges = graph.get(candidate1);
          edges.set(candidate2, (edges.get(candidate2) || 0) + 1);
          graph.set(candidate1, edges);
        }
        
        // Add edge from candidate2 to candidate1
        if (graph.has(candidate2)) {
          const edges = graph.get(candidate2);
          edges.set(candidate1, (edges.get(candidate1) || 0) + 1);
          graph.set(candidate2, edges);
        }
      }
    }
  });
  
  return graph;
}

/**
 * Apply the TextRank algorithm to the graph
 * @param {Map} graph - Co-occurrence graph
 * @returns {Map} - Map of candidates to their TextRank scores
 */
function applyTextRank(graph, iterations = 30, dampingFactor = 0.85) {
  const nodes = Array.from(graph.keys());
  const scores = new Map();
  
  // Initialize scores to 1.0
  nodes.forEach(node => {
    scores.set(node, 1.0);
  });
  
  // Run the algorithm for a fixed number of iterations
  for (let i = 0; i < iterations; i++) {
    const newScores = new Map();
    
    // Update score for each node
    nodes.forEach(node => {
      let score = 1 - dampingFactor; // Base score
      
      // Get all nodes pointing to this node
      const neighbors = Array.from(graph.get(node).keys());
      
      // Sum contribution from each neighbor
      neighbors.forEach(neighbor => {
        // Weight between neighbor and node
        const weight = graph.get(neighbor).get(node) || 0;
        
        // Sum of weights from neighbor to all its neighbors
        const sumWeights = Array.from(graph.get(neighbor).values())
          .reduce((sum, w) => sum + w, 0);
        
        // Add contribution if neighbor has outgoing edges
        if (sumWeights > 0) {
          score += dampingFactor * weight / sumWeights * scores.get(neighbor);
        }
      });
      
      newScores.set(node, score);
    });
    
    // Update scores for next iteration
    nodes.forEach(node => {
      scores.set(node, newScores.get(node));
    });
  }
  
  return scores;
}